/// <amd-module name="Test/Analytics/TestAnalyticInsightsReportView/ControllerViews/TestInsightsReportView" />
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import { AnnouncementActions } from "Test/Analytics/Common/Actions/AnnouncementActions";
import { AnnouncementStore } from "Test/Analytics/Common/Stores/AnnouncementStore";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import * as TcmPerfScenarios from "Test/Analytics/Common/Utils/TCMPerfScenarios";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { TestInsightsReportViewActions } from "Test/Analytics/TestAnalyticInsightsReportView/Actions/TestInsightsReportViewActions";
import { TestInsightsReportViewActionsCreator } from "Test/Analytics/TestAnalyticInsightsReportView/Actions/TestInsightsReportViewActionsCreator";
import * as Resources from "Test/Analytics/TestAnalyticInsightsReportView/Resources";
import {
    ITestInsightsReportViewState,
    TestInsightsReportViewStore
} from "Test/Analytics/TestAnalyticInsightsReportView/Stores/TestInsightsReportViewStore";
import { TestInsightsTrendChartActions } from "Test/Analytics/TestAnalyticsTrendChart/Actions/TestInsightsTrendChartActions";
import { TestInsightsTrendChartActionsCreator } from "Test/Analytics/TestAnalyticsTrendChart/Actions/TestInsightsTrendChartActionsCreator";
import { TestInsightsTrendChart } from "Test/Analytics/TestAnalyticsTrendChart/ControllerViews/TestInsightsTrendChart";
import { TestInsightsTrendChartStore } from "Test/Analytics/TestAnalyticsTrendChart/Stores/TestInsightsTrendChartStore";
import { TestInsightsTestHistoryListActions } from "Test/Analytics/TestInsightsHistoryList/Actions/TestInsightsTestHistoryListActions";
import { TestInsightsTestHistoryListActionsCreator } from "Test/Analytics/TestInsightsHistoryList/Actions/TestInsightsTestHistoryListActionsCreator";
import { TestInsightsTestHistoryList } from "Test/Analytics/TestInsightsHistoryList/ControllerViews/TestInsightsTestHistoryList";
import { TestInsightsTestHistoryListStore } from "Test/Analytics/TestInsightsHistoryList/Stores/TestInsightsTestHistoryListStore";
import { ContributionSource, IViewContextData, ViewContext } from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { ITestResultTreeData } from "Test/common-util/Contracts/ViewContracts";
import { ConfigurationToolbarActions } from "Test/ConfigurationToolbar/Actions/ConfigurationToolbarActions";
import { ConfigurationToolbarActionsCreator } from "Test/ConfigurationToolbar/Actions/ConfigurationToolbarActionsCreator";
import { ConfigurationToolbarStore } from "Test/ConfigurationToolbar/Store/ConfigurationToolbarStore";
import { FilterMetadataActions } from "Test/MetadataHandler/Actions/FilterMetadataActions";
import { FilterMetadataActionsCreator } from "Test/MetadataHandler/Actions/FilterMetadataActionsCreator";
import { FilterMetadataStore } from "Test/MetadataHandler/Store/FilterMetadataStore";
import { announce } from "VSS/Core/Util/Accessibility";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { ISplitterProps, Splitter, SplitterElementPosition } from "VSSUI/Components/Splitter";
import { Tooltip } from "VSSUI/TooltipEx";

export interface IReportViewProps extends CommonTypes.IReportComponentProps {
    onBackNavigation?: () => void;
    testReportContext: CommonTypes.ITestReportContext;
    testContext: CommonTypes.ITestContext;
    confValues?: CommonTypes.IReportConfiguration;
}

interface IExtendedTestInsightsReportViewState extends ITestInsightsReportViewState {
    minWidth: number;
}

export class TestInsightsReportView extends VssComponent<IReportViewProps, IExtendedTestInsightsReportViewState> {
    private _announcementStore: AnnouncementStore;

    private _testInsightsReportViewStore: TestInsightsReportViewStore;
    private _testInsightsReportViewActionsCreator: TestInsightsReportViewActionsCreator;

    private _filterMetadataActionCreator: FilterMetadataActionsCreator;
    private _filterMetadataStore: FilterMetadataStore;

    private _configurationToolbarStore: ConfigurationToolbarStore;
    private _configurationToolbarActionsCreator: ConfigurationToolbarActionsCreator;

    private _trendChartStore: TestInsightsTrendChartStore;
    private _trendChartActionCreator: TestInsightsTrendChartActionsCreator;

    private _testHistoryListStore: TestInsightsTestHistoryListStore;
    private _testHistoryListActionsCreator: TestInsightsTestHistoryListActionsCreator;

    private _perfService: IVssPerformanceService;

    private _activeTestItem: HTMLElement;
    constructor(props: IReportViewProps, context: IVssComponentContext) {
        super(props);

        let workflow = 0;
        if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Build) {
            workflow = CommonTypes.Workflow.Build;
        } else if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Release) {
            workflow = CommonTypes.Workflow.Release;
        }

        const defaultConfValues =
            this.props.confValues ||
            ({
                groupBy: CommonTypes.GroupBy.TestRun,
                trendBy: CommonTypes.TrendBy.Days,
                period: CommonTypes.Period.Days_14,
                outcomes: [CommonTypes.TestOutcome.Failed, CommonTypes.TestOutcome.Passed],
                workflow: workflow,
                configuredFilters: {}
            } as CommonTypes.IReportConfiguration);

        const announcementActions = new AnnouncementActions();
        this._announcementStore = new AnnouncementStore(announcementActions);
        this._announcementStore.addChangedListener(this._onAnnouncementUpdate);

        const filterMetadataActions = new FilterMetadataActions();
        this._filterMetadataActionCreator = new FilterMetadataActionsCreator(filterMetadataActions, context.pageContext);
        this._filterMetadataStore = new FilterMetadataStore(filterMetadataActions);

        const configurationToolbarActions = new ConfigurationToolbarActions();
        this._configurationToolbarStore = new ConfigurationToolbarStore(
            configurationToolbarActions,
            this.props.confValues ? this.props.confValues : defaultConfValues
        );
        this._configurationToolbarActionsCreator = new ConfigurationToolbarActionsCreator(configurationToolbarActions);

        const trendChartActions: TestInsightsTrendChartActions = new TestInsightsTrendChartActions();
        this._trendChartStore = new TestInsightsTrendChartStore(trendChartActions);
        this._trendChartActionCreator = new TestInsightsTrendChartActionsCreator(trendChartActions, announcementActions, context.pageContext);

        const testHistoryListActions: TestInsightsTestHistoryListActions = new TestInsightsTestHistoryListActions();
        this._testHistoryListStore = new TestInsightsTestHistoryListStore(
            testHistoryListActions,
            filterMetadataActions,
            this.props.testReportContext.contextType
        );
        this._testHistoryListActionsCreator = new TestInsightsTestHistoryListActionsCreator(
            testHistoryListActions,
            announcementActions,
            context.pageContext
        );

        this._perfService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._perfService.startScenario(TcmPerfScenarios.TestAX_TestInsightsReport, true);

        let filterMetadataPromise: Promise<void> = Promise.resolve();
        if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Release) {
            filterMetadataPromise = this._filterMetadataActionCreator.updateReleaseDefinitionMetadata(this.props.testReportContext.definitionId);
        }
        const trendChartPromise: Promise<void> = this._trendChartActionCreator.updateTrendChart(
            this.props.testReportContext,
            this.props.testContext,
            defaultConfValues
        );
        const testHistoryPromise: Promise<void> = this._testHistoryListActionsCreator.updateTestHistory(
            this.props.testReportContext,
            this.props.testContext,
            defaultConfValues
        );

        Promise.all([filterMetadataPromise, trendChartPromise, testHistoryPromise]).then(
            () => {
                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_TestInsightsReport, true);
            },
            error => {
                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_TestInsightsReport, true, undefined, {
                    status: "error"
                });
            }
        );

        const telemetryData: { [key: string]: any } = {
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[defaultConfValues.period].toString()
        };

        publishEvent(
            context.pageContext,
            TcmTelemetryConstants.featureTestAX_TestInsightsReport,
            telemetryData,
            this.props.testReportContext,
            defaultConfValues
        );

        const testInsightsReportViewActions: TestInsightsReportViewActions = new TestInsightsReportViewActions();
        this._testInsightsReportViewStore = new TestInsightsReportViewStore(trendChartActions, testHistoryListActions, testInsightsReportViewActions);
        this._testInsightsReportViewActionsCreator = new TestInsightsReportViewActionsCreator(testInsightsReportViewActions);

        this.state = this._testInsightsReportViewStore.getState() as IExtendedTestInsightsReportViewState;
    }

    public componentWillUnmount() {
        this._configurationToolbarStore.dispose();
        this._trendChartStore.dispose();
        this._testHistoryListStore.dispose();
        this._testInsightsReportViewStore.dispose();
        this._announcementStore.removeChangedListener(this._onAnnouncementUpdate);
    }

    public componentDidMount() {
        this._testInsightsReportViewStore.addChangedListener(this._onStoreUpdate);
    }

    public render(): JSX.Element {
        const areFiltersApplied = ConfigurationUtility.areFiltersApplied(
            this._configurationToolbarStore.getState().reportConfigurationValues.configuredFilters
        );
        if (this.state.viewType === CommonTypes.ViewType.ErrorView || this.state.viewType === CommonTypes.ViewType.NoTestDataView) {
            return (
                <div>
                    {this._renderConfigurationToolbar()}

                    {this.state.viewType === CommonTypes.ViewType.ErrorView && (
                        <AnalyticsUnavailableMessage
                            imageName={"ServiceError.svg"}
                            message={Resources.AnalyticsErrorMessage}
                            suggestion={this.state.errorText}
                            ariaDescription={Resources.AnalyticsErrorMessage}
                        />
                    )}

                    {this.state.viewType === CommonTypes.ViewType.NoTestDataView && (
                        <>
                            <AnalyticsUnavailableMessage
                                imageName={"testresults-not-found.svg"}
                                message={areFiltersApplied ? Resources.NoResultsMessage : Resources.AnalyticsTestResultsNotFoundMessage}
                                suggestion={
                                    areFiltersApplied ? Resources.NoResultsSuggestionMessage : Resources.AnalyticsTestResultsNotFoundSuggestion
                                }
                                ariaDescription={areFiltersApplied ? Resources.NoResultsMessage : Resources.AnalyticsTestResultsNotFoundMessage}
                            />
                            {publishEvent(
                                this.context.pageContext,
                                TcmTelemetryConstants.featureTestAX_NoTestResults,
                                {},
                                this.props.testReportContext,
                                this._configurationToolbarStore.getState().reportConfigurationValues
                            )}
                        </>
                    )}
                </div>
            );
        } else {
            return (
                <div className="new-testinsights-analytics-report-view flex-column">
                    {this._renderConfigurationToolbar()}

                    <Splitter {...this._getSplitterProps()} />
                </div>
            );
        }
    }

    private _getSplitterProps(): ISplitterProps {
        const splitterProps: ISplitterProps = {
            className: "horizontal hub-splitter testinsights-analytics-overlay-panel-component right-fix",
            fixedElement: SplitterElementPosition.Far,
            onRenderNearElement: this._getLeftPane,
            nearElementClassName: "overview-panel scrollable-container flex-column"
        };
        if (this.state.showDetailsPane) {
            splitterProps.onRenderFarElement = this._getRightPane;
            splitterProps.farElementClassName = "details-panel";
        }
        return splitterProps;
    }

    private _getLeftPane = (): JSX.Element => {
        return (
            <>
                <TestInsightsTrendChart
                    className="testinsight-aggregatereports-trendchart curved-border-white-background"
                    style={{ minWidth: this.state.minWidth }}
                    store={this._trendChartStore}
                    actionCreator={this._trendChartActionCreator}
                />

                <TestInsightsTestHistoryList
                    store={this._testHistoryListStore}
                    style={{ minWidth: this.state.minWidth }}
                    actionCreator={this._testHistoryListActionsCreator}
                    testReportContext={this.props.testReportContext}
                    testContext={this.props.testContext}
                    onGridRowClick={(item: ITestResultTreeData) => {
                        this._testInsightsReportViewActionsCreator.setTestResultTreeData(item);
                    }}
                    onMinWidthChange={this._handleMinWidthChange}
                    handleActiveRow={this._handleActiveRow}
                />
            </>
        );
    };

    private _getRightPane = (): JSX.Element => {
        const viewContextData: IViewContextData =
            this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Release
                ? ({ viewContext: ViewContext.Release } as IViewContextData)
                : ({ viewContext: ViewContext.Build } as IViewContextData);
        viewContextData.contributionSource = ContributionSource.AX;
        return (
            <div className="overlay-right-section">
                <Tooltip text={Resources.CloseDetailsPane}>
                    <div className="overlay-panel-close-button">
                        <Button subtle={true} ariaLabel={Resources.CloseDetailsPane} iconProps={{ iconName: "Cancel" }} onClick={this._closePanel} />
                    </div>
                </Tooltip>

                {this.state.detailsPanelErrorMessage && (
                    <AnalyticsUnavailableMessage
                        imageName={this.state.detailsPanelErrorStatus === 404 ? "testdetails-not-found.svg" : "ServiceError.svg"}
                        message={
                            this.state.detailsPanelErrorStatus === 404 ? Resources.DataNotAvailableErrorMessage : Resources.AnalyticsErrorMessage
                        }
                        suggestion={this.state.detailsPanelErrorMessage}
                        ariaDescription={
                            this.state.detailsPanelErrorStatus === 404 ? Resources.DataNotAvailableErrorMessage : Resources.AnalyticsErrorMessage
                        }
                    />
                )}

                {!this.state.detailsPanelErrorMessage && (
                    <WrappedComponent
                        dependencies={["ms.vss-test-web.test-resultdetails", "ms.vss-test-web.test-result-details-tab-items"]}
                        wrappedType="test-result-details"
                        testResultTreeData={this.state.selectedTestResultTreeData}
                        paneView={"debug"}
                        viewContext={viewContextData}
                        isFullScreen={false}
                        showHistory={false}
                        showHeader={true}
                        hideTestCaseTitle={false}
                        showBugAndLink={true}
                        linkedStackTrace={false}
                        onError={this._onDetailsPainError}
                    />
                )}
            </div>
        );
    };

    private _closePanel = () => {
        this._testInsightsReportViewActionsCreator.closeDetailsPanel();
    };

    private _onConfigurationToolbarChanged = (changedConfValues: CommonTypes.IReportConfiguration) => {
        this._trendChartActionCreator.updateTrendChart(this.props.testReportContext, this.props.testContext, changedConfValues);
        this._testHistoryListActionsCreator.updateTestHistory(this.props.testReportContext, this.props.testContext, changedConfValues);
    };

    private _onDetailsPainError = (error: any) => {
        if (!!error) {
            if (this.state.detailsPanelErrorMessage != error.message) {
                this.setState({
                    detailsPanelErrorMessage: error.message,
                    detailsPanelErrorStatus: error.status
                });
            }
        }
    };

    private _handleMinWidthChange = (minWidth: number) => {
        if (this.state && (!this.state.minWidth || this.state.minWidth !== minWidth)) {
            this.setState({ minWidth: minWidth });
        }
    };

    private _onStoreUpdate = () => {
        const prevDetailsPanelState = this.state.showDetailsPane;
        this.setState(this._testInsightsReportViewStore.getState(), () => {
            if (prevDetailsPanelState !== this.state.showDetailsPane) {
                // Needed to ensure that the grid presentation is re-drawn to fit the
                // container size.
                this._fireWindowResize();
                this._focusActiveTestItem();
            }
        });
    };

    private _fireWindowResize() {
        const event = document.createEvent("Event");
        event.initEvent("resize", false, true);
        window.dispatchEvent(event);
    }

    private _renderConfigurationToolbar = (): JSX.Element => {
        return (
            <WrappedComponent
                dependencies={["ms.vss-test-analytics-web.configuration-toolbar"]}
                wrappedType="configuration-toolbar"
                store={this._configurationToolbarStore}
                actionCreator={this._configurationToolbarActionsCreator}
                testReportContext={this.props.testReportContext}
                onConfigurationChange={this._onConfigurationToolbarChanged}
                isTestInsightsView={true}
                testContext={this.props.testContext}
                filterMetadataActionCreator={this._filterMetadataActionCreator}
                filterMetadataStore={this._filterMetadataStore}
                onBackNavigation={this.props.onBackNavigation}
            />
        );
    };

    private _onAnnouncementUpdate = () => {
        announce(this._announcementStore.getState().announcementText);
    };

    private _handleActiveRow = (_activeTestItem: HTMLElement) => {
        this._activeTestItem = _activeTestItem;
    };

    private _focusActiveTestItem = (): void => {
        if (this._activeTestItem) {
            this._activeTestItem.focus();
        }
    };
}

Components.add("test-insights-report-view", TestInsightsReportView);
